import {
  Heilbehandlungsschutz,
  SpartenType,
  TaasTkvV1Data,
  TierAntrag,
  Tierart,
  Tiergeschlecht,
  Tierhaltung,
} from '@ps20/bff-models';
import {
  convertTkvTaasBausteinToTarifrisikoschluessel,
  TierartEnum,
} from '@ps20-frontend/frontend-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import { convertDateFormatFromLocalStateToShortIso } from '@ps20-frontend/utils';
import {
  GeschlechtEnum,
  KatzenhaltungEnum,
  Versicherungsnehmer,
} from '../../reducers/versicherungsnehmer.reducer';
import { IAppState } from '../../appState';

export function buildTaasTkvV1DataForStrecke(state: IAppState): TaasTkvV1Data {
  const tkvData: TaasTkvV1Data = generateBasicTaasTkvV1Data(state);
  tkvData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertTkvTaasBausteinToTarifrisikoschluessel,
  );
  return tkvData;
}

export function buildTaasTkvV1DataForShop(state: IAppState): TaasTkvV1Data {
  const tkvData: TaasTkvV1Data = generateBasicTaasTkvV1Data(state);
  tkvData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertTkvTaasBausteinToTarifrisikoschluessel,
  );
  return tkvData;
}

function generateBasicTaasTkvV1Data(state: IAppState): TaasTkvV1Data {
  const tierart: Tierart = convertToBffTierart(state.versicherungsnehmer.tierart);
  const tierhaltung: Tierhaltung = convertToBffTierhaltung(state.versicherungsnehmer.katzenhaltung);
  const sublimit: number = mapSublimit(
    state.produkt.produktlinien.find(produktlinie => produktlinie.selected).zusatzoptionen[0]
      .eigenschaft,
  );

  return {
    hasHeilbehandlungen: state.versicherungsnehmer.behandlungen.ohneOperation,
    isKastriert: state.versicherungsnehmer.isTierKastriert,
    spartenType: SpartenType.TIERKRANKEN,
    anzVorschaedenOp: state.versicherungsnehmer.anzahlOperationen,
    geburtsdatumTier: convertDateFormatFromLocalStateToShortIso(
      state.versicherungsnehmer.geburtsdatumTier,
    ),
    kennungVorvers: false,
    tierart: tierart,
    tiergeschlecht:
      state.versicherungsnehmer.geschlecht === GeschlechtEnum.MAENNLICH
        ? Tiergeschlecht.MAENNLICH
        : Tiergeschlecht.WEIBLICH,
    tierrasse: state.versicherungsnehmer.rasse.value,
    plzVN: state.risikoort.plz,
    salesProducts: null,
    tierhaltung: tierhaltung,
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    sublimit: sublimit,
    urbeginndatum: state.vertragsoptionen.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
    fullCoverage: null,
  };
}

export function convertToBffTierhaltung(katzenhaltung: KatzenhaltungEnum): Tierhaltung {
  switch (katzenhaltung) {
    case KatzenhaltungEnum.WOHNUNGSKATZE:
      return Tierhaltung.WOHNUNGSKATZE;
    case KatzenhaltungEnum.FREIGAENGER:
      return Tierhaltung.FREIGAENGER;
    default:
      return null;
  }
}

export function convertToBffTierart(tierart: TierartEnum): Tierart {
  switch (tierart) {
    case TierartEnum.HUND:
      return Tierart.VRHUND;
    case TierartEnum.KATZE:
      return Tierart.VRKATZE;
    case TierartEnum.PFERD:
      return Tierart.VRPFERD;
    default:
      return null;
  }
}

export function convertToBffTiergeschlecht(geschlecht: GeschlechtEnum): Tiergeschlecht {
  switch (geschlecht) {
    case GeschlechtEnum.MAENNLICH:
      return Tiergeschlecht.MAENNLICH;
    case GeschlechtEnum.WEIBLICH:
      return Tiergeschlecht.WEIBLICH;
    default:
      return null;
  }
}

export function mapSublimit(eigenschaft: string): number {
  switch (eigenschaft) {
    case Heilbehandlungsschutz.BIS_2000_50:
    case Heilbehandlungsschutz.BIS_2000_100:
      return 2000;
    case Heilbehandlungsschutz.BIS_5000_100:
      return 5000;
  }
}

export function mapTierAntrag(versicherungsnehmer: Versicherungsnehmer): TierAntrag {
  return versicherungsnehmer.diagnose
    ? {
        tierart: null,
        geschlecht: null,
        diagnosenAntrag: versicherungsnehmer.diagnose,
      }
    : null;
}

export function mapHeilbehandlungsschutzToDruckString(eigenschaft: Heilbehandlungsschutz): string {
  if (eigenschaft === Heilbehandlungsschutz.BIS_2000_50) {
    return 'bis 2.000 EUR + 50 EUR Vorsorgezuschuss';
  } else if (eigenschaft === Heilbehandlungsschutz.BIS_5000_100) {
    return 'bis 5.000 EUR + 100 EUR Vorsorgezuschuss';
  }
  return 'bis 2.000 EUR + 100 EUR Vorsorgezuschuss';
}
